/**
 * Table palette — for the canvas and, identically, the PDF (`lib/pdf/render.ts`).
 * Originally mirrored from the server's `services/theme.py`.
 *
 * All three are cool-toned as requested: the "orange" is a desaturated clay
 * rather than a saturated orange, so the three sit together on one floor plan
 * without any of them shouting over the others.
 */

import type { TableColor } from './types'

export interface Swatch {
  /** Table top fill. */
  surface: string
  /** Table outline. */
  edge: string
  /** Table name text. */
  ink: string
  /** Empty chair fill. */
  seat: string
  /** Chair outline. */
  seatEdge: string
  /** Occupied-chair ring and highlights. */
  accent: string
  label: string
}

export const TABLE_SWATCHES: Record<TableColor, Swatch> = {
  blue: {
    surface: '#E4EEF8',
    edge: '#7FA6D0',
    ink: '#1E3B60',
    seat: '#F4F8FC',
    seatEdge: '#9DBBD9',
    accent: '#4C7FB5',
    label: 'Glacier blue',
  },
  pink: {
    surface: '#F3E5EE',
    edge: '#C68CAF',
    ink: '#54233F',
    seat: '#FBF4F8',
    seatEdge: '#D9AEC6',
    accent: '#A9628B',
    label: 'Frosted rose',
  },
  orange: {
    surface: '#F7E8DE',
    edge: '#D0977B',
    ink: '#5F3521',
    seat: '#FDF6F1',
    seatEdge: '#E0B69E',
    accent: '#B4714F',
    label: 'Cool clay',
  },
}

export const TABLE_COLORS: TableColor[] = ['blue', 'pink', 'orange']

export function swatchFor(color: TableColor): Swatch {
  return TABLE_SWATCHES[color] ?? TABLE_SWATCHES.blue
}

type Paint = Exclude<keyof Swatch, 'label'>
const PAINTS: Paint[] = ['surface', 'edge', 'ink', 'seat', 'seatEdge', 'accent']

function screenSwatch(color: TableColor): Swatch {
  const swatch = { ...TABLE_SWATCHES[color] }
  for (const paint of PAINTS) swatch[paint] = `var(--swatch-${color}-${paint})`
  return swatch
}

const SCREEN_SWATCHES: Record<TableColor, Swatch> = {
  blue: screenSwatch('blue'),
  pink: screenSwatch('pink'),
  orange: screenSwatch('orange'),
}

/**
 * The swatch for the screen: every paint is a CSS variable (`tokens.css`), so
 * the canvas follows the light or dark theme without re-rendering a table. In
 * the light theme the variables hold exactly `TABLE_SWATCHES`. The PDF uses
 * `swatchFor` — paper has no dark mode.
 */
export function screenSwatchFor(color: TableColor): Swatch {
  return SCREEN_SWATCHES[color] ?? SCREEN_SWATCHES.blue
}

/** Deterministic avatar tint for guests without a photo. */
export function avatarTint(seed: string): string {
  const palette = ['#4C7FB5', '#A9628B', '#B4714F', '#5B7F72', '#6C6BA8', '#A8734C']
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return palette[hash % palette.length]
}

export function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase()
}
